import {useLazyQuery} from "@apollo/client";
import {ForwardedRef, forwardRef, useCallback, useContext, useMemo} from "react";
import {components, GroupBase, OptionProps, OptionsOrGroups, SelectInstance} from "react-select";
import {useAsyncPaginate, useComponents} from "react-select-async-paginate";

import {CDNImage} from "@/components/cdnImage/CDNImage.tsx";
import {Select} from "@/components/select/Select.tsx";
import {SelectProps} from "@/components/select/Select.types.ts";
import {useStableLoadingState} from "@/components/select/useStableLoadingState.ts";
import {
  ThirdPartyDropdownIndicator,
  ThirdPartyIndicatorSeparator,
  ThirdPartyNoOptionsMessage,
  ThirdPartyValue,
} from "@/components/thirdPartyGroupSelect/ThirdPartyGroupSelect.tsx";
import {ThirdPartySubComponentSelectContext} from "@/components/thirdPartySubComponentSelect/ThirdPartySubComponentSelectContext.tsx";
import {
  GQL_THIRD_PARTY_COMPONENTS_V2,
  ThirdPartyComponent,
  ThirdPartyComponentList,
  ThirdPartyService,
} from "@/lib/queries/checks.ts";

import styles from "@/pages/checks/components/form/fields/components/ThirdPartyGroup.module.scss";

const ThirdPartySubComponentOption = ({data, children, ...props}: OptionProps<ThirdPartyComponent>) => {
  const group = useContext(ThirdPartySubComponentSelectContext);
  return (
    group && (
      <components.Option {...props} data={data}>
        <div className="d-flex align-items-center flex-wrap row-gap-2">
          <CDNImage className={styles.componentIconL} alt={`${group.group} logo`} src={group.logo_src} />
          <div className="pulseTextBodySmall">{children}</div>
        </div>
      </components.Option>
    )
  );
};

type ThirdPartySubComponentSelectProps = SelectProps<ThirdPartyComponent> & {
  group: ThirdPartyService;
  title?: string;
  excludeIds?: string[];
};

export const ThirdPartySubComponentSelect = forwardRef(
  (
    {group, title, excludeIds, ...props}: ThirdPartySubComponentSelectProps,
    ref: ForwardedRef<SelectInstance<ThirdPartyComponent>>
  ) => {
    const [getComponents] = useLazyQuery<ThirdPartyComponentList>(GQL_THIRD_PARTY_COMPONENTS_V2);
    const loadOptions = useCallback(
      async (search: string, prevOptions: OptionsOrGroups<ThirdPartyComponent, GroupBase<ThirdPartyComponent>>) => {
        const {data} = await getComponents({
          variables: {
            limit: 20,
            offset: prevOptions.length,
            group: group.id,
            title,
            subtitle: search,
            excludeIds,
          },
        });
        const items = data?.cloudStatusComponents?.items || [];
        const total = data?.cloudStatusComponents?.total || 0;
        return {options: items, hasMore: prevOptions.length + items.length < total};
      },
      [getComponents, group.id, title, excludeIds]
    );
    const excludeIdsKey = useMemo(() => excludeIds?.join(",") || "", [excludeIds]);
    const paginateResult = useStableLoadingState(
      useAsyncPaginate<ThirdPartyComponent, GroupBase<ThirdPartyComponent>, undefined>(
        {loadOptions, debounceTimeout: 500},
        [excludeIdsKey]
      )
    );
    const paginateComponents = useComponents<ThirdPartyComponent, GroupBase<ThirdPartyComponent>, false>({
      Option: ThirdPartySubComponentOption,
      SingleValue: ThirdPartyValue<ThirdPartyComponent>(props.placeholder),
      IndicatorSeparator: ThirdPartyIndicatorSeparator,
      DropdownIndicator: ThirdPartyDropdownIndicator<ThirdPartyComponent>,
      NoOptionsMessage: ThirdPartyNoOptionsMessage<ThirdPartyComponent>("component"),
    });
    return (
      <ThirdPartySubComponentSelectContext.Provider value={group}>
        <Select
          ref={ref}
          getOptionValue={({id}) => id}
          getOptionLabel={({display_sub_title, sub_title}) => display_sub_title || sub_title || ""}
          isSearchable
          components={paginateComponents}
          {...props}
          {...paginateResult}
          menuPosition="fixed"
        />
      </ThirdPartySubComponentSelectContext.Provider>
    );
  }
);
